import React, { useState } from 'react'
import { supabase } from '../supabase'
import { isTestAccount } from '../utils/testMode'

const INPUT_CSS = `
  .sn-input { background: #FFFFFF; border: none; box-shadow: var(--shadow-1), inset 0 1px 3px rgba(56,62,48,0.05); transition: box-shadow var(--t-fast) var(--ease); }
  .sn-input:focus { outline: none; box-shadow: 0 0 0 3px rgba(61,92,60,0.30), var(--shadow-1); }
`

const s = {
  wrap: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  logo: { fontFamily: 'Fraunces, serif', fontSize: '30px', fontWeight: 300, letterSpacing: '-0.3px', color: '#20241F', marginBottom: '36px' },
  logoEm: { color: '#3D5C3C' },
  card: { padding: '32px 28px', width: '100%', maxWidth: '400px' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '28px', fontWeight: 300, letterSpacing: '-0.3px', marginBottom: '6px' },
  titleEm: { color: '#3D5C3C' },
  sub: { fontSize: '13px', color: '#73736A', marginBottom: '24px', lineHeight: 1.6 },
  label: { marginBottom: '7px', display: 'block' },
  input: { width: '100%', borderRadius: '14px', padding: '13px 14px', fontSize: '16px', color: '#20241F', marginBottom: '14px' },
  btn: { width: '100%', marginTop: '4px' },
  btnGoogle: { width: '100%', marginBottom: '16px' },
  divider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' },
  divLine: { flex: 1, height: '1px', background: 'rgba(0,0,0,0.06)' },
  divText: { fontSize: '12px', color: '#73736A' },
  error: { background: '#FAEAEA', color: '#C95B5B', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', marginBottom: '14px' },
  footer: { textAlign: 'center', marginTop: '16px', fontSize: '13px', color: '#73736A' },
  link: { color: '#3D5C3C', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }
}

export default function Login({ onSuccess, onSignup, onResumePaid }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('login')  // login | forgot | forgot-sent | resume
  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    // Warm copy + the two doors that fix most failed logins (QA 2026-11-01 #4).
    if (error) setError(/invalid login credentials/i.test(error.message)
      ? "That email and password don't match. You can reset your password below, or, if you paid but never finished setting up, use the recovery link at the bottom."
      : error.message)
    else onSuccess()
    setLoading(false)
  }

  const handleGoogle = async () => {
    // Surface OAuth init failures (QA 2026-11-01 #5: a silent no-op button).
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' })
    if (error) setError('Google sign-in could not start: ' + error.message)
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    if (!email.trim()) { setError('Enter your email first.'); return }
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    })
    setLoading(false)
    if (error) { setError(error.message); return }
    setMode('forgot-sent')
  }

  const handleResume = async (e) => {
    e.preventDefault()
    if (!email.trim()) { setError('Enter the email you paid with.'); return }
    setLoading(true)
    setError('')
    // TESTING ONLY: robot accounts (+fable/+qa/+robot emails) skip the payment
    // check so automated end-to-end walks can self-provision. Any real email
    // goes through the normal paid-resume verification below, unchanged.
    if (isTestAccount(email.trim())) {
      setLoading(false)
      onResumePaid && onResumePaid(email.trim())
      return
    }
    try {
      const res = await fetch('/.netlify/functions/resume-paid-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()
      setLoading(false)
      if (data.ok) {
        onResumePaid && onResumePaid(email.trim())
      } else {
        setError(data.error || "We couldn't find a payment under that email. If you think this is wrong, contact us and we'll sort it out.")
      }
    } catch (err) {
      setLoading(false)
      setError('Something went wrong. Please try again.')
    }
  }

  if (mode === 'forgot' || mode === 'forgot-sent') {
    return (
      <div style={s.wrap}>
        <style>{INPUT_CSS}</style>
        <div style={s.logo}>Sensify<em style={s.logoEm}>.</em></div>
        <div className="sn-card sn-rise" style={s.card}>
          {mode === 'forgot-sent' ? (
            <>
              <div style={s.title}>Check your <em style={s.titleEm}>email.</em></div>
              <div style={s.sub}>If an account exists for {email}, we've sent a link to reset your password. It may take a minute to arrive.</div>
              <div style={s.footer}><span style={s.link} onClick={() => { setMode('login'); setError('') }}>← Back to sign in</span></div>
            </>
          ) : (
            <>
              <div style={s.title}>Reset your <em style={s.titleEm}>password.</em></div>
              <div style={s.sub}>Enter your email and we'll send you a reset link.</div>
              {error && <div style={s.error}>{error}</div>}
              <form onSubmit={handleForgot}>
                <label className="sn-micro" style={s.label}>Email</label>
                <input className="sn-input" style={s.input} type="email" autoComplete="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                <button className="sn-btn" style={s.btn} type="submit" disabled={loading}>{loading ? 'Sending…' : 'Send reset link →'}</button>
              </form>
              <div style={s.footer}><span style={s.link} onClick={() => { setMode('login'); setError('') }}>← Back to sign in</span></div>
            </>
          )}
        </div>
      </div>
    )
  }

  if (mode === 'resume') {
    return (
      <div style={s.wrap}>
        <style>{INPUT_CSS}</style>
        <div style={s.logo}>Sensify<em style={s.logoEm}>.</em></div>
        <div className="sn-card sn-rise" style={s.card}>
          <div style={s.title}>Finish setting <em style={s.titleEm}>up.</em></div>
          <div style={s.sub}>Already paid but never created your account? Enter the email you used at checkout and we'll pick up where you left off.</div>
          {error && <div style={s.error}>{error}</div>}
          <form onSubmit={handleResume}>
            <label className="sn-micro" style={s.label}>Email you paid with</label>
            <input className="sn-input" style={s.input} type="email" autoComplete="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
            <button className="sn-btn" style={s.btn} type="submit" disabled={loading}>{loading ? 'Checking…' : 'Continue →'}</button>
          </form>
          <div style={s.footer}><span style={s.link} onClick={() => { setMode('login'); setError('') }}>← Back to sign in</span></div>
        </div>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <style>{INPUT_CSS}</style>
      <div style={s.logo}>Sensify<em style={s.logoEm}>.</em></div>
      <div className="sn-card sn-rise" style={s.card}>
        <div style={s.title}>Welcome <em style={s.titleEm}>back.</em></div>
        <div style={s.sub}>Sign in to continue your program.</div>
        <button className="sn-btn sn-btn-quiet" style={s.btnGoogle} onClick={handleGoogle}>
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <div style={s.divider}><div style={s.divLine}></div><span style={s.divText}>or</span><div style={s.divLine}></div></div>
        {error && <div style={s.error}>{error}</div>}
        <form onSubmit={handleLogin}>
          <label className="sn-micro" style={s.label}>Email</label>
          <input className="sn-input" style={s.input} type="email" autoComplete="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
          <label className="sn-micro" style={s.label}>Password</label>
          <input className="sn-input" style={s.input} type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
          <button className="sn-btn" style={s.btn} type="submit" disabled={loading}>{loading ? 'Signing in...' : 'Sign in →'}</button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <span style={{ ...s.link, fontSize: '13px' }} onClick={() => { setMode('forgot'); setError('') }}>Forgot password?</span>
        </div>
        <div style={s.footer}>Don't have an account? <span style={s.link} onClick={onSignup}>Get started</span></div>
        <div style={{ ...s.footer, marginTop: '8px', fontSize: '12px' }}>Paid but never finished setting up? <span style={s.link} onClick={() => { setMode('resume'); setError('') }}>Continue here</span></div>
      </div>
    </div>
  )
}
